import {
  evaluateApplicationSourceSufficiencyGate,
  readProjectApplicationSourceAssessment,
  type ApplicationGenerationLevel,
  type ApplicationSourceDimensionId,
  type StudioApplicationSourceSufficiencyGate,
  type StudioSourceClarificationQuestion,
} from '@/features/source-assessment/source-assessment-contract'

export type GenerationEntrySourceSufficiencyAction =
  | 'proceed'
  | 'ask-first'

export type GenerationEntrySourceSufficiencyReason =
  | 'not-required'
  | 'ready'
  | 'clarification-required'
  | 'assessment-required'

export interface GenerationEntrySourceSufficiencyDecision {
  schemaVersion: 'studio-generation-entry-source-sufficiency-decision.v1'
  action: GenerationEntrySourceSufficiencyAction
  reason: GenerationEntrySourceSufficiencyReason
  sourceGateStatus: StudioApplicationSourceSufficiencyGate['status']
  assessmentRevision: string | null
  requestedGenerationLevel: ApplicationGenerationLevel | null
  allowedGenerationLevel: ApplicationGenerationLevel | null
  blockerDimensionIds: ApplicationSourceDimensionId[]
  clarificationRequestIds: string[]
  questions: StudioSourceClarificationQuestion[]
  evidenceRefs: string[]
  scoreIsAuthority: false
}

const BLOCKED_GENERATION_LEVEL_SEVERITY: Record<string, number> = {
  'visual-preview': 5,
  'client-prototype': 4,
  'contract-bound-client': 3,
  'integration-ready': 2,
  'production-ready': 1,
}

function compareQuestions(
  left: StudioSourceClarificationQuestion,
  right: StudioSourceClarificationQuestion,
): number {
  const severityDifference =
    (BLOCKED_GENERATION_LEVEL_SEVERITY[right.blockedGenerationLevel] ?? 0) -
    (BLOCKED_GENERATION_LEVEL_SEVERITY[left.blockedGenerationLevel] ?? 0)
  if (severityDifference !== 0) return severityDifference
  const priorityDifference = right.priority - left.priority
  if (priorityDifference !== 0) return priorityDifference
  if (left.id < right.id) return -1
  if (left.id > right.id) return 1
  return 0
}

function selectBlockingQuestions(
  project: unknown,
): StudioSourceClarificationQuestion[] {
  const assessment = readProjectApplicationSourceAssessment(project)
  if (!assessment) return []

  const clarificationIdByBlockingDimension = new Map(
    assessment.dimensions
      .filter(
        (dimension) =>
          dimension.requiredForTarget && !dimension.acceptedForTarget,
      )
      .map((dimension) => [
        dimension.dimensionId,
        dimension.clarificationRequestId,
      ]),
  )

  return assessment.clarification.displayQuestions
    .filter(
      (question) =>
        clarificationIdByBlockingDimension.get(question.dimensionId) ===
        question.id,
    )
    .sort(compareQuestions)
    .slice(0, 3)
    .map((question) => ({
      ...question,
      decisionOptions: [...question.decisionOptions],
    }))
}

/**
 * Applies source sufficiency at generation entry without promoting a score into
 * authority. Source-derived generation without a normalized assessment stays
 * ask-first and deliberately has no client-fabricated clarification question.
 */
export function evaluateGenerationEntrySourceSufficiency(
  project: unknown,
): GenerationEntrySourceSufficiencyDecision {
  const gate = evaluateApplicationSourceSufficiencyGate(project)
  const assessment = readProjectApplicationSourceAssessment(project)
  const action =
    gate.status === 'ready' || gate.status === 'not-required'
      ? 'proceed'
      : 'ask-first'
  const reason: GenerationEntrySourceSufficiencyReason =
    gate.status === 'not-required'
      ? 'not-required'
      : gate.status === 'ready'
        ? 'ready'
        : assessment
          ? 'clarification-required'
          : 'assessment-required'

  return {
    schemaVersion: 'studio-generation-entry-source-sufficiency-decision.v1',
    action,
    reason,
    sourceGateStatus: gate.status,
    assessmentRevision: gate.assessmentRevision,
    requestedGenerationLevel: gate.requestedGenerationLevel,
    allowedGenerationLevel: gate.maxSafeGenerationLevel,
    blockerDimensionIds: [...gate.blockerDimensionIds],
    clarificationRequestIds: [...gate.clarificationRequestIds],
    questions: action === 'ask-first' ? selectBlockingQuestions(project) : [],
    evidenceRefs: [...gate.evidenceRefs],
    scoreIsAuthority: false,
  }
}
